import * as React from 'react';
import { useEffect } from 'react';
import {
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    FormHelperText
} from '@mui/material';

import { useField, useFormikContext } from 'formik';
import { RadioGroup } from '../../FormFields';

const STATUS_OPTIONS = [
    { label: 'Not Begun',   value: 'Not Begun' },
    { label: 'Stalled',     value: 'Stalled' },
    { label: 'In Progress', value: 'In Progress' },
    { label: 'Complete',    value: 'Complete' },
];

// Visually hidden style — label exists in the DOM for screen readers but is invisible
const visuallyHidden = {
    position: 'absolute',
    width: '1px',
    height: '1px',
    padding: 0,
    margin: '-1px',
    overflow: 'hidden',
    clip: 'rect(0,0,0,0)',
    whiteSpace: 'nowrap',
    border: 0,
};

function StatusRow({ field, meta, getIsError }) {
    const labelId = `${field.name}-label`;
    return (
        <TableRow
            role="radiogroup"
            aria-labelledby={labelId}
        >
            <TableCell
                component="th"
                scope="row"
                id={labelId}
                align="center"
                style={{ minWidth: 120, fontWeight: 'bold' }}
            >
                {field.label}
                {getIsError(field.name) && meta.error?.[field.name] && (
                    <FormHelperText sx={{ textAlign: 'center' }} error>
                        {meta.error[field.name]}
                    </FormHelperText>
                )}
            </TableCell>

            {STATUS_OPTIONS.map((option) => (
                <TableCell key={option.value} align="center" style={{ minWidth: 170 }}>
                    <RadioGroup
                        name={field.name}
                        rowLabel={field.label}
                        data={[{
                            label: "",
                            value: option.value,
                            // Pass a sx or labelProps to hide the label visually
                            labelProps: { style: visuallyHidden }
                        }]}
                        fullWidth
                    />
                </TableCell>
            ))}
        </TableRow>
    );
}

export default function ProjectStatus(props) {
    const {
        formField: {
            feasibilityStudy,
            preliminaryDesign,
            nepaReview,
            finalDesign,
            funding,
            rowAcquisition,
            construction,
            scopeIdentified,
            fundingAcquired,
            researcherIdentified,
            studyInProgress,
            studyComplete,
        }, values } = props;

    const [, meta] = useField(props);
    const { touched, errors, setFieldValue } = useFormikContext();

    const isProjectStudy = typeof values?.isProjectStudy === 'boolean'
        ? values.isProjectStudy
        : String(values?.isProjectStudy || '').toLowerCase() === 'yes';

    const studyFieldDefs = [
        scopeIdentified,
        fundingAcquired,
        researcherIdentified,
        studyInProgress,
        studyComplete,
    ];

    const nonStudyFieldDefs = [
        feasibilityStudy,
        preliminaryDesign,
        nepaReview,
        finalDesign,
        funding,
        rowAcquisition,
        construction,
    ];

    const studyFields    = studyFieldDefs.map(f => f.name);
    const nonStudyFields = nonStudyFieldDefs.map(f => f.name);

    useEffect(() => {
        const fieldsToClear = isProjectStudy ? nonStudyFields : studyFields;
        fieldsToClear.forEach((fieldName) => {
            if (values?.[fieldName]) {
                setFieldValue(fieldName, '', false);
            }
        });
    }, [isProjectStudy, values, setFieldValue]);

    const getIsError = (fieldName) => {
        return touched[fieldName] && Boolean(errors[fieldName]);
    };

    const activeFields = isProjectStudy ? studyFieldDefs : nonStudyFieldDefs;

    return (
        <Grid item xs={12} sx={{ marginRight: '10px' }}>
            <TableContainer>
                <Table stickyHeader aria-label="Project status">
                    <TableHead>
                        <TableRow>
                            <TableCell
                                component="th"
                                scope="col"
                                style={{ minWidth: 120 }}
                                aria-label="Status Category"
                            />
                            {STATUS_OPTIONS.map((option) => (
                                <TableCell
                                    key={option.value}
                                    component="th"
                                    scope="col"
                                    style={{ minWidth: 120, fontWeight: 'bold' }}
                                >
                                    {option.label}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {activeFields.map((field) => (
                            <StatusRow
                                key={field.name}
                                field={field}
                                meta={meta}
                                getIsError={getIsError}
                            />
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Grid>
    );
}